import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SidebarProvider } from './contexts/SidebarContext';
import { ToastProvider } from './contexts/ToastContext';
import LoginBloomberg from './components/LoginBloomberg';
import Dashboard from './components/Dashboard';
import AdminNavigation from './components/AdminNavigation';
import WalletPage from './components/Wallet/WalletPage';
import SwapPage from './components/Swap/SwapPage';
import BotControl from './components/Bot/BotControl';
import SettingsPage from './components/Settings/SettingsPage';
import SubscriptionPage from './components/Subscription/SubscriptionPage';
import ReferralPage from './components/Referral/ReferralPage';
import { VaultDashboard } from './components/vault';
import OnboardingFlow from './components/Onboarding/OnboardingFlow';
import './App.css';

// Protected Route component
function ProtectedRoute({ children, skipOnboardingCheck = false }: { children: React.ReactNode; skipOnboardingCheck?: boolean }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-400">Loading...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Redirect to onboarding if not completed (unless this is the onboarding route)
  if (!skipOnboardingCheck && !user.has_completed_onboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}

function AppContent() {
  const { user, isLoading, pendingPlanId, markOnboardingComplete } = useAuth();

  // Determine where to redirect authenticated users
  const getAuthenticatedRedirect = () => {
    // Redirect to onboarding if not completed
    if (user && !user.has_completed_onboarding) {
      return "/onboarding";
    }
    if (pendingPlanId) {
      return "/subscription";
    }
    return "/vault";
  };

  const handleOnboardingComplete = () => {
    markOnboardingComplete();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-400">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public route */}
      <Route path="/login" element={user ? <Navigate to={getAuthenticatedRedirect()} replace /> : <LoginBloomberg />} />

      {/* Onboarding route - protected but skips onboarding check */}
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute skipOnboardingCheck>
            {user?.has_completed_onboarding ? (
              <Navigate to="/vault" replace />
            ) : (
              <OnboardingFlow onComplete={handleOnboardingComplete} />
            )}
          </ProtectedRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/wallet"
        element={
          <ProtectedRoute>
            <WalletPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/deposit"
        element={<Navigate to="/wallet" replace />}
      />
      <Route
        path="/swap"
        element={
          <ProtectedRoute>
            <SwapPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            {user?.is_admin ? <AdminNavigation /> : <Navigate to="/vault" replace />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/bot"
        element={
          <ProtectedRoute>
            <BotControl />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription"
        element={
          <ProtectedRoute>
            <SubscriptionPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/referral"
        element={
          <ProtectedRoute>
            <ReferralPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vault"
        element={
          <ProtectedRoute>
            <VaultDashboard />
          </ProtectedRoute>
        }
      />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to={user ? getAuthenticatedRedirect() : "/login"} replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <SidebarProvider>
        <ToastProvider position="top-right">
          <Router>
            <AppContent />
          </Router>
        </ToastProvider>
      </SidebarProvider>
    </AuthProvider>
  );
}

export default App;
